import type { FetchApi } from '../client';

export class GetAccessTokenError extends Error {
  constructor(public error: string) {
    super(
      `[Hitachi AEMP 2/getAccessTokenHitachi] Call failed with error: ${error}`,
    );
  }
}

export type OAuthSessionInit = {
  oauthUrl: string;
  sessionId: string;
};

export type OAuthSetupLink = {
  code: string;
  url: string;
};

export type OAuthSessionResult =
  | { status: 'completed'; integrationId: number }
  | { status: 'error'; reason: string };

export class OAuthRequestError extends Error {
  constructor(public readonly status: number) {
    super(`[catalog/oauth] Call failed with status: ${status}`);
  }
}

const statusOf = (error: unknown) =>
  (error as { originalResponse?: Response })?.originalResponse?.status ?? 0;

// The client resolves with the raw response instead of throwing on a 401.
const rejectUnauthorized = <T>(response: T | Response): T => {
  if (response instanceof Response) {
    throw new OAuthRequestError(response.status);
  }
  return response;
};

export const oauthApi = (fetchApi: FetchApi) => ({
  initIntegrationOAuth({
    dataSourceCode,
    integrationName,
    integrationCredentials,
    redirectUri,
    token,
  }: {
    dataSourceCode: string;
    integrationName: string;
    integrationCredentials: Record<string, unknown>;
    redirectUri: string;
    token: string;
  }): Promise<OAuthSessionInit> {
    return fetchApi
      .setToken(token)
      .post('/catalog/integrations/oauth', {
        dataSourceCode,
        integrationName,
        integrationCredentials,
        redirectUri,
      })
      .catch((error) => {
        throw new OAuthRequestError(statusOf(error));
      })
      .then(rejectUnauthorized);
  },
  initIntegrationOAuthForUpdate({
    integrationId,
    redirectUri,
    token,
  }: {
    integrationId: number;
    redirectUri: string;
    token: string;
  }): Promise<OAuthSessionInit> {
    return fetchApi
      .setToken(token)
      .post(`/catalog/integrations/${integrationId}/oauth`, { redirectUri })
      .catch((error) => {
        throw new OAuthRequestError(statusOf(error));
      })
      .then(rejectUnauthorized);
  },
  /** Resolves to `null` while the third party has not called the callback back yet. */
  getOAuthSessionResult({
    sessionId,
    token,
  }: {
    sessionId: string;
    token: string;
  }): Promise<OAuthSessionResult | null> {
    return fetchApi
      .setToken(token)
      .get(`/catalog/integrations/oauth/${sessionId}`, { cache: false })
      .catch((error) => {
        if (statusOf(error) === 404) return null;
        throw new OAuthRequestError(statusOf(error));
      })
      .then(rejectUnauthorized);
  },
  getIntegrationSetupLinks({
    integrationId,
    redirectUri,
    token,
  }: {
    integrationId: number;
    redirectUri: string;
    token: string;
  }): Promise<{ links: OAuthSetupLink[] }> {
    return fetchApi
      .setToken(token)
      .get(
        `/catalog/integrations/${integrationId}/setup-links?redirectUri=${encodeURIComponent(redirectUri)}`,
      )
      .catch((error) => {
        throw new OAuthRequestError(statusOf(error));
      })
      .then(rejectUnauthorized);
  },
  getAccessTokenHitachiAndCreateIntegration(
    clientId: string,
    clientSecret: string,
    name: string,
    authorizationCode: string,
    callbackOriginURL: string,
    token: string,
  ) {
    return fetchApi
      .setToken(token)
      .get(
        `/app/oAuth/hitachi/getAccessTokenAndCreateIntegration?name=${name}&clientId=${clientId}&clientSecret=${clientSecret}&authorizationCode=${authorizationCode}&callbackOriginURL=${callbackOriginURL}`,
      )
      .catch((error) => {
        const errorMessage = error?.message
          ? error.message
          : 'Unable to get a Hitachi access token for this integration.';
        throw new GetAccessTokenError(errorMessage);
      });
  },
  getAccessTokenHitachiAndEditIntegration(
    integrationId: number,
    authorizationCode: string,
    callbackOriginURL: string,
    token: string,
  ) {
    return fetchApi
      .setToken(token)
      .get(
        `/app/oAuth/hitachi/getAccessTokenAndEditIntegration?integrationId=${integrationId}&authorizationCode=${authorizationCode}&callbackOriginURL=${callbackOriginURL}`,
      )
      .catch((error) => {
        const errorMessage = error?.message
          ? error.message
          : 'Unable to get a Hitachi access token for this integration.';
        throw new GetAccessTokenError(errorMessage);
      });
  },
  checkExistingHitachiIntegration(
    clientId: string,
    clientSecret: string,
    token: string,
  ) {
    return fetchApi
      .setToken(token)
      .get(
        `/app/oAuth/hitachi/checkExistingIntegration?clientId=${clientId}&clientSecret=${clientSecret}`,
      )
      .catch((error) => {
        const errorMessage = error?.message
          ? error.message
          : 'Credentials are already integrated';
        throw new GetAccessTokenError(errorMessage);
      });
  },
  getAccessTokenWackerNeusonAndCreateIntegration(
    clientId: string,
    clientSecret: string,
    username: string,
    password: string,
    name: string,
    authorizationCode: string,
    callbackOriginURL: string,
    token: string,
  ) {
    return fetchApi
      .setToken(token)
      .get(
        `/oauth/wacker-neuson/create-integration?name=${name}&authorizationCode=${authorizationCode}&clientId=${clientId}&clientSecret=${clientSecret}&username=${username}&password=${encodeURIComponent(password)}&callbackOriginURL=${callbackOriginURL}`,
      )
      .catch((error) => {
        const errorMessage = error?.message
          ? error.message
          : 'Unable to get a Wacker Neuson access token for this integration.';
        throw new GetAccessTokenError(errorMessage);
      });
  },
  getAccessTokenWackerNeusonAndEditIntegration(
    integrationId: number,
    authorizationCode: string,
    callbackOriginURL: string,
    token: string,
  ) {
    return fetchApi
      .setToken(token)
      .get(
        `/oauth/wacker-neuson/update-integration?integrationId=${integrationId}&authorizationCode=${authorizationCode}&callbackOriginURL=${callbackOriginURL}`,
      )
      .catch((error) => {
        const errorMessage = error?.message
          ? error.message
          : 'Unable to get a Wacker Neuson access token for this integration.';
        throw new GetAccessTokenError(errorMessage);
      });
  },
  checkExistingWackerNeusonIntegration(
    clientId: string,
    clientSecret: string,
    username: string,
    password: string,
    token: string,
  ) {
    return fetchApi
      .setToken(token)
      .get(
        `/oauth/wacker-neuson/check-existing-integration?clientId=${clientId}&clientSecret=${clientSecret}&username=${username}&password=${encodeURIComponent(password)}`,
      )
      .catch((error) => {
        const errorMessage = error?.message
          ? error.message
          : 'Credentials are already integrated';
        throw new GetAccessTokenError(errorMessage);
      });
  },
});
